import React, { memo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { createSelector } from 'reselect'
import cx from 'classnames'

import { makeStyles, useTheme } from '@material-ui/core/styles'
import { Grid, Drawer } from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'

import useViewportSize from '~/components/useViewportSize'
import ProductDetails from './ProductDetails/ProductDetails'

import track from '~/track'
import actions, { TYPES } from '~/actions'

const useStyles = makeStyles((theme) => ({
  paper: {
    position: 'absolute'
  },
  drawerMobile: {
    width: '100%',
    overflow: 'hidden',
    flexShrink: 0
  },
  drawerDesktop: {
    width: theme.gofind.asideWidth,
    overflow: 'hidden',
    flexShrink: 0
  }
}))

const selector = createSelector(
  (state) => state.app.currentStore,
  (state) => state.app.drawerProductDetails,
  (state) => state.app.loadingProduct,
  (state) => state.app.isOpenDrawerProductDetails,
  (state) => state.config,
  (currentStore, drawerProductDetails, loadingProduct, isOpenDrawerProductDetails, config) => ({
    currentStore,
    drawerProductDetails,
    loadingProduct,
    isOpenDrawerProductDetails,
    config
  })
)

function DrawerProduct({ position }) {
  const history = useHistory()
  const {
    currentStore: store,
    drawerProductDetails: data,
    loadingProduct: isLoading,
    isOpenDrawerProductDetails: open,
    config
  } = useSelector(selector)

  const theme = useTheme()
  const classes = useStyles()
  const dispatch = useDispatch()
  const viewport = useViewportSize()
  const isMobile = viewport.width < theme.gofind.desktopMaxWidth

  const handleClose = useCallback(() => {
    dispatch(actions.dialog.error(false))
    dispatch(actions.closeDrawerProduct())

    if (history.location.state?.prevPath === '/') {
      dispatch(actions.updateApp({ view: TYPES.VIEW_PRODUCTS }))
    } else {
      dispatch(actions.updateApp({ view: TYPES.VIEW_STORES }))
    }

    !store && history.location.state?.prevPath?.includes('/loja/')
      ? !!config.showContextViewStore
        ? history.push('/produto/todos')
        : history.push('/teste')
      : history.goBack()
  }, [store])

  const handleFind = useCallback(() => {
    dispatch(actions.dialog.error(false))
    dispatch(actions.closeDrawerProduct())

    track('encontrarLojaFisica', {
      category: 'produto',
      name: data.product.description
    })

    setTimeout(() => {
      history.replace(`/produto/${data.product.gtin}`)
    }, 300)
  }, [data])

  return (
    <Drawer
      hideBackdrop
      anchor="left"
      id="drawer-product-details"
      open={open}
      onClose={handleClose}
      classes={{
        paper: cx(classes.paper, {
          [classes.drawerMobile]: isMobile,
          [classes.drawerDesktop]: !isMobile
        })
      }}
      PaperProps={{ 'data-cy': 'drawer-product-details' }}
      ModalProps={{
        container: document.getElementById('gofind-finder') || document.body,
        style: { position: 'absolute', width: isMobile ? '100%' : theme.gofind.asideWidth }
      }}
    >
      {data ? (
        <ProductDetails
          data={data}
          onClose={handleClose}
          onFind={handleFind}
          store={store}
          position={position}
          config={config}
          loading={isLoading}
        />
      ) : (
        <Grid style={{ padding: 16 }}>
          <Skeleton variant="rect" />
          <br />
          <Skeleton variant="rect" height={60} />
          <br />
          <Skeleton variant="rect" width={100} />
          <br />
          <Grid
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 16
            }}
          >
            <Skeleton variant="circle" width={200} height={200} />
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="80%" />

            <Grid
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 16
              }}
            >
              <Skeleton variant="rect" height={50} width={150} />
              <Skeleton variant="rect" height={50} width={150} />
            </Grid>

            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="100%" />
          </Grid>
        </Grid>
      )}
    </Drawer>
  )
}

export default memo(DrawerProduct)
